import type { ColumnState, GridState } from 'ag-grid-community';
import { isEqual } from 'lodash-es';

interface LegacyComponent {
  state?: ColumnState[];
  sort?: Record<string, string[]>;
}

interface LegacyRowGroupEntry {
  expanded: boolean;
  children: Record<string, LegacyRowGroupEntry>;
}

interface LegacyShape {
  components?: Record<string, LegacyComponent> | null;
  rowGroupModel?: Record<string, LegacyRowGroupEntry> | null;
  tableFilters?: Record<string, unknown> | null;
  gridState?: GridState;
  origin?: unknown;
  order?: { sections?: string[]; components?: Record<string, string[]> };
  [key: string]: unknown;
}

interface AdapterOptions {
  onLegacyHit?: () => void;
}

/**
 * Translates a legacy-shape view config payload into the new shape:
 * `gridState` replaces `components['table']`, `rowGroupModel`, and
 * `tableFilters`. A persisted `gridState` is trusted only when ag-grid v35+
 * wrote it (see `isV35GridState`); a pre-v35 or version-less `gridState` is
 * ignored and rebuilt from the legacy keys, so pre-v35 state is never
 * applied to a v35+ grid. On the trusted v35+ path the legacy keys are still
 * stripped from the returned object, and when they disagree with `gridState`
 * on a user-intent slice they are treated as newer — written by a rolled-back
 * pre-v35 client — and `gridState` is rebuilt from them so post-rollback edits
 * survive. `origin` is passed through untouched: reset-view was removed from
 * the UI, so nothing reads the origin's table state and there's nothing to
 * adapt. Never throws.
 */
type AdapterResult<T> = Omit<T, 'components' | 'rowGroupModel' | 'tableFilters'> & {
  gridState?: GridState;
};

// ag-grid stamps `version` on every getState() (StateService → migrateGridStateModel
// always sets state.version to the current ag-grid version). So a real v35+ save always
// carries `version: '35.x'` or later; a missing / older / unparseable version means a pre-v35
// (or hand-built) state we must not hand to a v35+ grid. (mirrors BrowserGuard.tsx)
// v35 state on v36 is pinned by initialGridState.v35SavedState.test.ts.
function isV35GridState(gridState: GridState): boolean {
  const major = parseInt(String(gridState.version ?? '').split('.')[0], 10);
  return Number.isFinite(major) && major >= 35;
}

export function adaptLegacyViewConfig<T extends object>(
  raw: T,
  options: AdapterOptions = {}
): AdapterResult<T> {
  if (raw == null || typeof raw !== 'object') {
    return raw as AdapterResult<T>;
  }
  const input = raw as unknown as LegacyShape;

  if (input.gridState !== undefined && isV35GridState(input.gridState)) {
    // Scrub historical corruption (null / null-colId entries written by old
    // serializers) out of the stored state before it is compared or returned, so
    // a null hole can neither crash the diff nor masquerade as a real user edit.
    const storedGridState = sanitizeGridState(input.gridState);
    const { components: _c, rowGroupModel: _r, tableFilters: _t, gridState: _g, ...rest } = input;
    // FD-3283: a rolled-back (pre-v35) client edits the legacy keys on save but
    // carries the v35 `gridState` along untouched. If the two disagree on a
    // user-intent slice, the legacy keys are newer (post-rollback edits) and must win.
    const legacyGridState = buildGridState(input.components?.table, input.rowGroupModel, input.tableFilters);
    // columnOrder required: a real (v33) save always writes full column state;
    // without it the rebuilt state can't represent columns and must not win.
    if (
      legacyGridState?.columnOrder &&
      userIntentSlicesDiffer(storedGridState ?? ({} as GridState), legacyGridState)
    ) {
      options.onLegacyHit?.();
      return { ...(rest as object), gridState: legacyGridState } as unknown as AdapterResult<T>;
    }
    return { ...(rest as object), gridState: storedGridState } as unknown as AdapterResult<T>;
  }

  // gridState absent OR pre-v35: rebuild from the legacy keys. `gridState` is
  // stripped from the spread — otherwise a stale pre-v35 state would survive
  // when there are no legacy keys to rebuild from.
  options.onLegacyHit?.();

  const tableComponent = input.components?.table;
  const gridState = buildGridState(tableComponent, input.rowGroupModel, input.tableFilters);

  const { components: _c, rowGroupModel: _r, tableFilters: _t, gridState: _g, ...rest } = input;

  const result: LegacyShape = { ...(rest as object) };
  if (gridState) {
    result.gridState = gridState;
  }

  return result as unknown as AdapterResult<T>;
}

function buildGridState(
  table: LegacyComponent | undefined,
  rowGroupModel: Record<string, LegacyRowGroupEntry> | null | undefined,
  tableFilters: Record<string, unknown> | null | undefined
): GridState | undefined {
  // Drop null/undefined holes up front: the same historical corruption that put
  // nulls in gridState slices can leave them here, and every filter/map below
  // dereferences colId — a raw null element would throw "reading 'colId'".
  const colState = (table?.state ?? []).filter((c): c is ColumnState => c != null);
  const hasAny =
    colState.length > 0 ||
    (rowGroupModel != null && Object.keys(rowGroupModel).length > 0) ||
    (tableFilters != null && Object.keys(tableFilters).length > 0);
  if (!hasAny) {
    return undefined;
  }

  const gridState: GridState = {};

  if (colState.length > 0) {
    const sizing = colState
      .filter((c) => c.colId != null && c.width != null)
      .map((c) => ({ colId: c.colId, width: c.width as number }));
    if (sizing.length) {
      gridState.columnSizing = { columnSizingModel: sizing };
    }

    const hidden = colState.filter((c) => c.hide && c.colId != null).map((c) => c.colId);
    if (hidden.length) {
      gridState.columnVisibility = { hiddenColIds: hidden };
    }

    const left = colState.filter((c) => c.pinned === 'left' && c.colId != null).map((c) => c.colId);
    const right = colState.filter((c) => c.pinned === 'right' && c.colId != null).map((c) => c.colId);
    if (left.length || right.length) {
      gridState.columnPinning = { leftColIds: left, rightColIds: right };
    }

    const order = colState.map((c) => c.colId).filter(Boolean);
    if (order.length) {
      gridState.columnOrder = { orderedColIds: order };
    }

    const grouped = colState
      .filter((c) => c.rowGroup && c.colId != null)
      .sort((a, b) => (a.rowGroupIndex ?? 0) - (b.rowGroupIndex ?? 0))
      .map((c) => c.colId);
    if (grouped.length) {
      gridState.rowGroup = { groupColIds: grouped };
    }

    const sortModel = colState
      .filter((c) => c.sort != null && c.colId != null)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((c) => ({ colId: c.colId, sort: c.sort as 'asc' | 'desc' }));
    if (sortModel.length) {
      gridState.sort = { sortModel };
    }
  }

  if (rowGroupModel != null && Object.keys(rowGroupModel).length > 0) {
    const expanded = flattenRowGroupExpansion(rowGroupModel);
    if (expanded.length) {
      gridState.rowGroupExpansion = { expandedRowGroupIds: expanded };
    }
  }

  if (tableFilters != null && Object.keys(tableFilters).length > 0) {
    gridState.filter = { filterModel: tableFilters };
  }

  return Object.keys(gridState).length === 0 ? undefined : gridState;
}

// Order-sensitive / set signatures; empty normalizes to undefined so a missing
// slice equals an empty one (e.g. `filterModel: {}` vs no tableFilters).
const seqSig = (arr: readonly (string | null | undefined)[] | undefined): string | undefined => {
  const v = (arr ?? []).filter((x): x is string => x != null && x !== '');
  return v.length ? v.join('\u0000') : undefined;
};
const setSig = (arr: readonly (string | null | undefined)[] | undefined): string | undefined => {
  const v = (arr ?? []).filter((x): x is string => x != null && x !== '').sort();
  return v.length ? v.join('\u0000') : undefined;
};

/**
 * True when the legacy keys and the stored `gridState` disagree on a
 * user-intent slice (order / visibility / pinning / grouping / sort / filter).
 * Sizing and rowGroupExpansion are deliberately ignored: width/expansion-only
 * edits made while rolled back are not worth re-deriving state over, and the
 * v35 projection round-trips them with cosmetic differences.
 */
function userIntentSlicesDiffer(stored: GridState, legacy: GridState): boolean {
  const storedFilter = stored.filter?.filterModel ?? {};
  const legacyFilter = legacy.filter?.filterModel ?? {};
  return (
    seqSig(stored.columnOrder?.orderedColIds) !== seqSig(legacy.columnOrder?.orderedColIds) ||
    setSig(stored.columnVisibility?.hiddenColIds) !== setSig(legacy.columnVisibility?.hiddenColIds) ||
    setSig(stored.columnPinning?.leftColIds) !== setSig(legacy.columnPinning?.leftColIds) ||
    setSig(stored.columnPinning?.rightColIds) !== setSig(legacy.columnPinning?.rightColIds) ||
    seqSig(stored.rowGroup?.groupColIds) !== seqSig(legacy.rowGroup?.groupColIds) ||
    seqSig(stored.sort?.sortModel?.map((s) => `${s.colId}:${s.sort}`)) !==
      seqSig(legacy.sort?.sortModel?.map((s) => `${s.colId}:${s.sort}`)) ||
    !isEqual(storedFilter, legacyFilter)
  );
}

function flattenRowGroupExpansion(tree: Record<string, LegacyRowGroupEntry>, prefix = ''): string[] {
  const out: string[] = [];
  for (const [key, entry] of Object.entries(tree)) {
    const path = prefix ? `${prefix}/${key}` : key;
    if (entry?.expanded) {
      out.push(path);
    }
    if (entry?.children && Object.keys(entry.children).length > 0) {
      out.push(...flattenRowGroupExpansion(entry.children, path));
    }
  }
  return out;
}

interface SanitizeOptions {
  onCorruptionDetected?: (slice: string) => void;
}

/**
 * Defensive pass over a `GridState` before handing it to ag-grid. Strips
 * null/undefined entries from every array slice and drops object entries
 * whose `colId` is missing. This guards against historical data corruption
 * (e.g. legacy serializers that wrote nulls into `columnSizingModel` or
 * `sortModel`) which would otherwise crash ag-grid's `StateService` with
 * "Cannot destructure property 'colId' of null".
 *
 * Returns the same reference when nothing needed cleaning, so React identity
 * comparisons downstream don't churn unnecessarily.
 */
export function sanitizeGridState(
  gridState: GridState | undefined,
  options: SanitizeOptions = {}
): GridState | undefined {
  if (!gridState || typeof gridState !== 'object') {
    return gridState;
  }

  let dirty = false;
  const flag = (slice: string) => {
    dirty = true;
    options.onCorruptionDetected?.(slice);
  };

  const out: GridState = { ...gridState };

  // String array slices: drop null / undefined / empty string entries.
  if (out.columnVisibility?.hiddenColIds) {
    const cleaned = cleanStringArray(out.columnVisibility.hiddenColIds);
    if (cleaned.changed) {
      flag('columnVisibility');
      out.columnVisibility = { hiddenColIds: cleaned.value };
    }
  }
  if (out.columnOrder?.orderedColIds) {
    const cleaned = cleanStringArray(out.columnOrder.orderedColIds);
    if (cleaned.changed) {
      flag('columnOrder');
      out.columnOrder = { orderedColIds: cleaned.value };
    }
  }
  if (out.rowGroup?.groupColIds) {
    const cleaned = cleanStringArray(out.rowGroup.groupColIds);
    if (cleaned.changed) {
      flag('rowGroup');
      out.rowGroup = { groupColIds: cleaned.value };
    }
  }
  if (out.rowGroupExpansion?.expandedRowGroupIds) {
    const cleaned = cleanStringArray(out.rowGroupExpansion.expandedRowGroupIds);
    if (cleaned.changed) {
      flag('rowGroupExpansion');
      out.rowGroupExpansion = { expandedRowGroupIds: cleaned.value };
    }
  }
  if (out.pivot?.pivotColIds) {
    const cleaned = cleanStringArray(out.pivot.pivotColIds);
    if (cleaned.changed) {
      flag('pivot');
      out.pivot = { ...out.pivot, pivotColIds: cleaned.value };
    }
  }
  if (out.columnGroup?.openColumnGroupIds) {
    const cleaned = cleanStringArray(out.columnGroup.openColumnGroupIds);
    if (cleaned.changed) {
      flag('columnGroup');
      out.columnGroup = { openColumnGroupIds: cleaned.value };
    }
  }

  if (out.columnPinning) {
    const leftCleaned = cleanStringArray(out.columnPinning.leftColIds);
    const rightCleaned = cleanStringArray(out.columnPinning.rightColIds);
    if (leftCleaned.changed || rightCleaned.changed) {
      flag('columnPinning');
      out.columnPinning = {
        leftColIds: leftCleaned.value,
        rightColIds: rightCleaned.value,
      };
    }
  }

  // Object array slices: drop null entries and entries with null colId.
  if (out.columnSizing?.columnSizingModel) {
    const cleaned = cleanColIdObjectArray(out.columnSizing.columnSizingModel);
    if (cleaned.changed) {
      flag('columnSizing.columnSizingModel');
      out.columnSizing = cleaned.value.length > 0 ? { columnSizingModel: cleaned.value } : undefined;
    }
  }

  if (out.sort?.sortModel) {
    const cleaned = cleanColIdObjectArray(out.sort.sortModel);
    if (cleaned.changed) {
      flag('sort.sortModel');
      out.sort = cleaned.value.length > 0 ? { sortModel: cleaned.value } : undefined;
    }
  }

  if (out.aggregation?.aggregationModel) {
    const cleaned = cleanColIdObjectArray(out.aggregation.aggregationModel);
    if (cleaned.changed) {
      flag('aggregation.aggregationModel');
      out.aggregation = cleaned.value.length > 0 ? { aggregationModel: cleaned.value } : undefined;
    }
  }

  // Single-object slice: drop entirely if colId is missing.
  if (out.focusedCell && (out.focusedCell.colId == null || out.focusedCell.colId === '')) {
    flag('focusedCell');
    out.focusedCell = undefined;
  }

  return dirty ? out : gridState;
}

interface CleanResult<T> {
  value: T[];
  changed: boolean;
}

function cleanStringArray(arr: readonly string[] | undefined): CleanResult<string> {
  if (!arr) {
    return { value: [], changed: false };
  }
  const out: string[] = [];
  let changed = false;
  for (const v of arr) {
    if (v == null || v === '') {
      changed = true;
      continue;
    }
    out.push(v);
  }
  return { value: out, changed };
}

function cleanColIdObjectArray<T extends { colId?: string | null }>(arr: readonly T[]): CleanResult<T> {
  const out: T[] = [];
  let changed = false;
  for (const entry of arr) {
    if (entry == null || entry.colId == null || entry.colId === '') {
      changed = true;
      continue;
    }
    out.push(entry);
  }
  return { value: out, changed };
}

/**
 * Reverse of `buildGridState`: projects a `GridState` back to the legacy-shape
 * `{ components.table, rowGroupModel, tableFilters }` keys. Used by
 * `serializeView` to dual-write both shapes during the rollback-safety window
 * so an older (rolled-back) frontend can still read views the new client saved.
 *
 * Only the legacy fields the forward path consumes are reproduced (width,
 * hide, pinned, rowGroup/rowGroupIndex, sort/sortIndex, colId). Other
 * `ColumnState` fields are not present in `GridState` and can't be recovered.
 * Old client doesn't read them from the saved view anyway, so this is fine.
 *
 * Returns an empty object when `gridState` is missing/empty — caller decides
 * whether to merge.
 */
export interface LegacyShapeProjection {
  components?: { table: LegacyComponent };
  rowGroupModel?: Record<string, LegacyRowGroupEntry>;
  tableFilters?: Record<string, unknown>;
}

export function buildLegacyShapeFromGridState(gridState: GridState | undefined): LegacyShapeProjection {
  if (!gridState || Object.keys(gridState).length === 0) {
    return {};
  }

  const projection: LegacyShapeProjection = {};

  const columnState = buildLegacyColumnState(gridState);
  if (columnState.length > 0) {
    projection.components = { table: { state: columnState, sort: {} } };
  }

  const expanded = gridState.rowGroupExpansion?.expandedRowGroupIds ?? [];
  if (expanded.length > 0) {
    // rowGroupModel is keyed by the plain group key; strip ag-grid's `row-group-<col>-` node-id prefix.
    const groupColIds = gridState.rowGroup?.groupColIds ?? [];
    const keys = expanded.map((id) => stripAutoGroupColPrefix(id, groupColIds));
    projection.rowGroupModel = unflattenRowGroupExpansion(keys);
  }

  const filterModel = gridState.filter?.filterModel;
  if (filterModel && Object.keys(filterModel).length > 0) {
    projection.tableFilters = filterModel as Record<string, unknown>;
  }

  return projection;
}

function buildLegacyColumnState(gridState: GridState): ColumnState[] {
  // Walk every slice and collect the union of colIds. Build one ColumnState
  // per colId, merging the relevant fields from each slice.
  const byColId = new Map<string, ColumnState>();

  const ensure = (colId: string): ColumnState => {
    let entry = byColId.get(colId);
    if (!entry) {
      entry = { colId };
      byColId.set(colId, entry);
    }
    return entry;
  };

  for (const sizing of gridState.columnSizing?.columnSizingModel ?? []) {
    if (sizing.colId && sizing.width != null) {
      ensure(sizing.colId).width = sizing.width;
    }
  }

  for (const colId of gridState.columnVisibility?.hiddenColIds ?? []) {
    ensure(colId).hide = true;
  }

  for (const colId of gridState.columnPinning?.leftColIds ?? []) {
    ensure(colId).pinned = 'left';
  }
  for (const colId of gridState.columnPinning?.rightColIds ?? []) {
    ensure(colId).pinned = 'right';
  }

  const groupColIds = gridState.rowGroup?.groupColIds ?? [];
  groupColIds.forEach((colId, idx) => {
    const entry = ensure(colId);
    entry.rowGroup = true;
    entry.rowGroupIndex = idx;
  });

  const sortModel = gridState.sort?.sortModel ?? [];
  sortModel.forEach((s, idx) => {
    if (!s.colId) {
      return;
    }
    const entry = ensure(s.colId);
    entry.sort = s.sort;
    entry.sortIndex = idx;
  });

  // Make every column's visibility explicit (FD-2977 rollback regression).
  // The legacy save path stored `getColumnState()`, which emitted `hide` as a
  // boolean for *every* column. The rolled-back client's merge starts every
  // column hidden and only un-hides one whose saved ColumnState carries
  // `hide: false`; a column with no `hide` key — or absent from the saved state
  // entirely — stays hidden, blanking the whole grid. So:
  //   1. `ensure` every colId in `columnOrder` (the full column set), so
  //      visible default-width columns aren't dropped from the projection.
  //   2. default every non-hidden entry to `hide: false`.
  // `columnVisibility.hiddenColIds` already set `hide: true` above. The forward
  // path (`buildGridState`) filters on truthy `hide`, so `false` is a no-op
  // there and the round-trip stays stable.
  for (const colId of gridState.columnOrder?.orderedColIds ?? []) {
    if (colId) {
      ensure(colId);
    }
  }
  for (const entry of byColId.values()) {
    if (entry.hide !== true) {
      entry.hide = false;
    }
  }

  // Preserve the ordering from columnOrder when present; otherwise fall back
  // to insertion order, which matches the order of slices walked above.
  const order = gridState.columnOrder?.orderedColIds;
  if (order && order.length > 0) {
    const ordered: ColumnState[] = [];
    const seen = new Set<string>();
    for (const colId of order) {
      const entry = byColId.get(colId);
      if (entry) {
        ordered.push(entry);
        seen.add(colId);
      }
    }
    // Append any colIds that appeared in other slices but weren't in columnOrder.
    for (const [colId, entry] of byColId) {
      if (!seen.has(colId)) {
        ordered.push(entry);
      }
    }
    return ordered;
  }

  return Array.from(byColId.values());
}

// Recover the plain key from ag-grid's `row-group-<col>-<key>` node id (keys may contain '-').
function stripAutoGroupColPrefix(id: string, groupColIds: readonly string[]): string {
  for (const colId of groupColIds) {
    const prefix = `row-group-${colId}-`;
    if (id.startsWith(prefix)) {
      return id.slice(prefix.length);
    }
  }
  return id;
}

function unflattenRowGroupExpansion(paths: string[]): Record<string, LegacyRowGroupEntry> {
  const root: Record<string, LegacyRowGroupEntry> = {};
  for (const path of paths) {
    const segments = path.split('/');
    let cursor = root;
    for (let i = 0; i < segments.length; i++) {
      const key = segments[i];
      const isLeaf = i === segments.length - 1;
      let entry = cursor[key];
      if (!entry) {
        entry = { expanded: false, children: {} };
        cursor[key] = entry;
      }
      if (isLeaf) {
        entry.expanded = true;
      }
      cursor = entry.children;
    }
  }
  return root;
}
